import { getOfflineDb } from './dbFactory'
import { deleteKey, listStorageKeys } from '../utils/storage'

// v2.2 findings #76: when a facility or production unit is deleted on the
// server, this browser kept its cached production rows, log copies and water
// tables (equipoa: 160 production rows, incl. lot "101", from a deleted
// facility). Reports and local screens could show them again.
//
// After a successful farm-list refresh, drop cached data whose production unit
// is not in the known list (server units plus local units still waiting to
// sync). Never runs on an empty list (e.g. a failed or partial load).

type KnownFarm = { id: string; productionUnits?: Array<{ id: string }> }

export type PurgeOrphanedLocalDataResult = {
  productionRows: number
  logs: number
  storageKeys: number
}

const UUID_LENGTH = 36
const DATASET_KEY_PREFIXES = ['water-v2-', 'water-migrated-v2-', 'production-unit-table-v2-']

/** `<prefix><farmId>-<unitId>[::tenant]` with uuid ids → the unit id, else null. */
export const parseDatasetKeyUnitId = (storageKey: string): string | null => {
  const base = storageKey.split('::')[0] ?? storageKey
  const prefix = DATASET_KEY_PREFIXES.find((candidate) => base.startsWith(candidate))
  if (!prefix) return null
  const rest = base.slice(prefix.length)
  if (rest.length !== UUID_LENGTH * 2 + 1 || rest[UUID_LENGTH] !== '-') return null
  return rest.slice(UUID_LENGTH + 1)
}

export const purgeOrphanedLocalData = async (farms: KnownFarm[]): Promise<PurgeOrphanedLocalDataResult> => {
  const result: PurgeOrphanedLocalDataResult = { productionRows: 0, logs: 0, storageKeys: 0 }
  const knownUnitIds = new Set<string>()
  for (const farm of farms) {
    for (const unit of farm.productionUnits ?? []) {
      if (unit?.id) knownUnitIds.add(String(unit.id))
    }
  }
  if (farms.length === 0 || knownUnitIds.size === 0) return result

  const isOrphan = (row: { productionUnitId?: unknown }): boolean => {
    const unitId = typeof row.productionUnitId === 'string' ? row.productionUnitId : ''
    return unitId !== '' && !knownUnitIds.has(unitId)
  }

  const db = getOfflineDb()

  const orphanRowKeys = (await db.productionTableRows.toArray()).filter(isOrphan).map((row) => row.pk)
  if (orphanRowKeys.length > 0) {
    await db.productionTableRows.bulkDelete(orphanRowKeys)
    result.productionRows = orphanRowKeys.length
  }

  for (const table of [db.feedLogs, db.mortalityLogs, db.weightSampleLogs, db.fishStockLogs, db.harvestLogs] as const) {
    const ids = ((await table.toArray()) as Array<{ id: string; productionUnitId?: string }>).filter(isOrphan).map((row) => row.id)
    if (ids.length > 0) {
      await table.bulkDelete(ids)
      result.logs += ids.length
    }
  }

  for (const key of listStorageKeys()) {
    const unitId = parseDatasetKeyUnitId(key)
    if (unitId && !knownUnitIds.has(unitId)) {
      deleteKey(key)
      result.storageKeys += 1
    }
  }

  return result
}
